import {
  createRootRoute,
  createRoute,
  createRouter,
} from "@tanstack/react-router";
import { AppShell } from "@/components/AppShell";
import { Dashboard } from "@/pages/Dashboard";
import { MovieList } from "@/pages/movies/MovieList";
import { MovieDetail } from "@/pages/movies/MovieDetail";
import { MovieCollections } from "@/pages/movies/MovieCollections";
import { MovieCollectionDetail } from "@/pages/movies/MovieCollectionDetail";
import { MovieImportLists } from "@/pages/movies/MovieImportLists";
import { Indexers } from "@/pages/settings/Indexers";
import { Tags } from "@/pages/settings/Tags";
import { DownloadClients } from "@/pages/settings/DownloadClients";
import { Quality } from "@/pages/settings/Quality";
import { CustomFormats } from "@/pages/settings/CustomFormats";
import { Tmdb } from "@/pages/settings/Tmdb";
import { Spotify } from "@/pages/settings/Spotify";
import { SystemSettings } from "@/pages/settings/System";
import { BulkRedownloadPage } from "@/pages/settings/BulkRedownload";
import { Search } from "@/pages/Search";
import { SavedSearches } from "@/pages/search/SavedSearches";
import { SavedSearchDetail } from "@/pages/search/SavedSearchDetail";
import { MusicSettings } from "@/pages/settings/Music";
import { MoviesSettings } from "@/pages/settings/Movies";
import { MoviesWanted } from "@/pages/movies/MoviesWanted";
import { TVSettings } from "@/pages/settings/TV";
import { TVWanted } from "@/pages/tv/TVWanted";
import { TVImportLists } from "@/pages/tv/TVImportLists";
import { SeriesList } from "@/pages/tv/SeriesList";
import { SeriesDetail } from "@/pages/tv/SeriesDetail";
import { NzbDownloading } from "@/pages/settings/NzbDownloading";
import { Subtitles } from "@/pages/settings/Subtitles";
import { Backups } from "@/pages/settings/Backups";
import { Notifications } from "@/pages/settings/Notifications";
import { PathMappings } from "@/pages/settings/PathMappings";
import { LibraryPermissions } from "@/pages/settings/LibraryPermissions";
import { McpTokens } from "@/pages/settings/McpTokens";
import { SubtitlesWanted } from "@/pages/subtitles/SubtitlesWanted";
import { NzbQueue } from "@/pages/nzb/Queue";
import { NzbHistory } from "@/pages/nzb/History";
import { Playlists } from "@/pages/music/Playlists";
import { Discover } from "@/pages/music/Discover";
import { ArtistList } from "@/pages/music/ArtistList";
import { ArtistDetail } from "@/pages/music/ArtistDetail";
import { AlbumDetail } from "@/pages/music/AlbumDetail";
import { MusicWanted } from "@/pages/music/MusicWanted";
import { MusicIntegrity } from "@/pages/music/Integrity";
import { MusicPlaylistImport } from "@/pages/music/PlaylistImport";
import { ComingSoon } from "@/pages/ComingSoon";
import { Calendar } from "@/pages/Calendar";
import { Health } from "@/pages/Health";

const rootRoute = createRootRoute({ component: AppShell });

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: Dashboard,
});

const moviesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/movies",
  component: MovieList,
});

const movieDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/movies/$id",
  component: MovieDetail,
});

const tvRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tv",
  component: SeriesList,
});

const seriesDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tv/$id",
  component: SeriesDetail,
});

const musicRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/music",
  component: ArtistList,
});

const artistDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/music/artists/$id",
  component: ArtistDetail,
});

const albumDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/music/albums/$id",
  component: AlbumDetail,
});

const activityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/activity",
  component: () => (
    <ComingSoon
      title="Activity"
      milestone="next round"
      hint="Will combine /api/v1/queue + /api/v1/grabs"
    />
  ),
});

const calendarRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/calendar",
  component: Calendar,
});

const healthRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/health",
  component: Health,
});

const indexersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/indexers",
  component: Indexers,
});

const tagsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/tags",
  component: Tags,
});

const downloadClientsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/download-clients",
  component: DownloadClients,
});

const tmdbRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/tmdb",
  component: Tmdb,
});

const spotifyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/spotify",
  component: Spotify,
});

const musicSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/music",
  component: MusicSettings,
});

const moviesSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/movies",
  component: MoviesSettings,
});

const moviesWantedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/movies/wanted",
  component: MoviesWanted,
});

const movieCollectionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/movies/collections",
  component: MovieCollections,
});

const movieCollectionDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/movies/collections/$id",
  component: MovieCollectionDetail,
});

const movieImportListsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/movies/import-lists",
  component: MovieImportLists,
});

const tvWantedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tv/wanted",
  component: TVWanted,
});

const tvImportListsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tv/import-lists",
  component: TVImportLists,
});

const musicWantedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/music/wanted",
  component: MusicWanted,
});

const musicIntegrityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/music/integrity",
  component: MusicIntegrity,
});

const musicPlaylistImportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/music/playlist-import",
  component: MusicPlaylistImport,
});

const tvSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/tv",
  component: TVSettings,
});

const searchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/search",
  component: Search,
});

const savedSearchesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/search/saved",
  component: SavedSearches,
});

const savedSearchDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/search/saved/$id",
  component: SavedSearchDetail,
});

const playlistsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/music/playlists",
  component: Playlists,
});

const discoverRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/music/discover",
  component: Discover,
});

const qualityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/quality",
  component: Quality,
});

const customFormatsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/quality/custom-formats",
  component: CustomFormats,
});

const notificationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/notifications",
  component: Notifications,
});

const nzbQueueRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/nzb/queue",
  component: NzbQueue,
});

const nzbHistoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/nzb/history",
  component: NzbHistory,
});

const nzbDownloadingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/nzb",
  component: NzbDownloading,
});

const subtitlesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/subtitles",
  component: Subtitles,
});

const backupsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/backups",
  component: Backups,
});

const systemSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/system",
  component: SystemSettings,
});

const bulkRedownloadRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/bulk-redownload",
  component: BulkRedownloadPage,
});

const libraryPermissionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/library-permissions",
  component: LibraryPermissions,
});

const subtitlesWantedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/subtitles/wanted",
  component: SubtitlesWanted,
});

const pathMappingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/path-mappings",
  component: PathMappings,
});

const mcpTokensRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/mcp-tokens",
  component: McpTokens,
});

const routeTree = rootRoute.addChildren([
  dashboardRoute,
  moviesRoute,
  moviesWantedRoute,
  movieCollectionsRoute,
  movieCollectionDetailRoute,
  movieImportListsRoute,
  movieDetailRoute,
  tvRoute,
  seriesDetailRoute,
  tvWantedRoute,
  tvImportListsRoute,
  tvSettingsRoute,
  musicRoute,
  activityRoute,
  calendarRoute,
  healthRoute,
  searchRoute,
  savedSearchesRoute,
  savedSearchDetailRoute,
  indexersRoute,
  tagsRoute,
  downloadClientsRoute,
  tmdbRoute,
  spotifyRoute,
  musicSettingsRoute,
  moviesSettingsRoute,
  qualityRoute,
  customFormatsRoute,
  notificationsRoute,
  nzbQueueRoute,
  nzbHistoryRoute,
  nzbDownloadingRoute,
  subtitlesRoute,
  subtitlesWantedRoute,
  backupsRoute,
  systemSettingsRoute,
  bulkRedownloadRoute,
  libraryPermissionsRoute,
  playlistsRoute,
  discoverRoute,
  artistDetailRoute,
  albumDetailRoute,
  musicWantedRoute,
  musicIntegrityRoute,
  musicPlaylistImportRoute,
  pathMappingsRoute,
  mcpTokensRoute,
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
